import type { TrendPoint } from '@ehr/shared';

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V123 tlChart): the Sign timeliness chart's geometry and the
 * reports' date wording. The windows live in components/NoteReports.tsx.
 */
export const CHART = { W: 760, H: 220, L: 40, R: 14, T: 12, B: 30 } as const;

export interface ChartPoint extends TrendPoint {
  x: number;
  y: number;
  /** Show the label under this point (about ten labels at most). */
  labelled: boolean;
}

/** Where each point sits: evenly across, 0% at the bottom and 100% at the top; one point centred. */
export function chartPoints(trend: TrendPoint[]): ChartPoint[] {
  const { W, H, L, R, T, B } = CHART;
  const n = trend.length;
  const every = Math.max(1, Math.ceil(n / 10));
  return trend.map((p, i) => ({
    ...p,
    x: n === 1 ? (L + W - R) / 2 : L + (i * (W - L - R)) / (n - 1),
    y: T + ((100 - p.pct) * (H - T - B)) / 100,
    labelled: i % every === 0,
  }));
}
/** The Y of a percent (grid lines at 0, 50 and 100). */
export const chartY = (pct: number) =>
  CHART.T + ((100 - pct) * (CHART.H - CHART.T - CHART.B)) / 100;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-10-17" as "Oct 17, 2026" (the session's own day, no time zone shift). */
export function sessionDay(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  return m ? `${MON[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : ymd;
}
/** "Oct 20, 2026, 2:30 AM" on this device's clock. */
export function signedStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
/** "Show 25 more of 40 left" (V141 tlMore). */
export const moreLabel = (left: number) => `Show ${Math.min(25, left)} more of ${left} left`;
